import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useHorizontalScrollFade } from '@/components/agent-chat';
type SuggestionItem = {
  id: string;
  label: string;
  icon: Icon;
};
type SuggestionListProps = {
  suggestions: SuggestionItem[];
  disabled?: boolean;
  onSelect: (id: string) => void;
  onHoverChange?: (id: string | null) => void;
  /** Present when more rows sit behind Show more; they follow the first two rows. */
  more?: {
    expanded: boolean;
    toggle: () => void;
  };
};
type PillRowsProps = Omit<SuggestionListProps, 'more'>;
const ROW_SIZE = 3;
const FIRST_ROWS = 2;
const FADE_PX = 32;

// The rows open and close the same way, closing a touch quicker.
const OPEN = {
  height: 'auto',
  opacity: 1,
  transition: {
    duration: 0.28,
    ease: 'easeInOut'
  }
} as const;
const CLOSED = {
  height: 0,
  opacity: 0,
  transition: {
    duration: 0.22,
    ease: 'easeInOut'
  }
} as const;
const buildFade = (fadeStart: boolean, fadeEnd: boolean) => `linear-gradient(to right, ${fadeStart ? 'transparent' : '#000'} 0, #000 ${fadeStart ? FADE_PX : 0}px, #000 calc(100% - ${fadeEnd ? FADE_PX : 0}px), ${fadeEnd ? 'transparent' : '#000'} 100%)`;
const toRows = (suggestions: SuggestionItem[]) => Array.from({
  length: Math.ceil(suggestions.length / ROW_SIZE)
}, (_, row) => suggestions.slice(row * ROW_SIZE, (row + 1) * ROW_SIZE));

// Rows of three on a desktop; a phone flattens them (display: contents) into one row it swipes.
const PillRows = t0 => {
  const $ = _c(18);
  const {
    suggestions,
    disabled,
    onSelect,
    onHoverChange
  } = t0;
  const {
    ref,
    canScrollBack,
    canScrollForward
  } = useHorizontalScrollFade(suggestions.length);
  let t1;
  if ($[0] !== canScrollBack || $[1] !== canScrollForward) {
    t1 = buildFade(canScrollBack, canScrollForward);
    $[0] = canScrollBack;
    $[1] = canScrollForward;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const maskImage = t1;
  let t2;
  if ($[3] !== maskImage) {
    t2 = {
      scrollbarWidth: "none",
      maskImage,
      WebkitMaskImage: maskImage
    };
    $[3] = maskImage;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== disabled || $[6] !== onHoverChange || $[7] !== onSelect || $[8] !== suggestions) {
    let t4;
    if ($[10] !== disabled || $[11] !== onHoverChange || $[12] !== onSelect) {
      t4 = row => <div key={row.map(_temp).join()} className="flex flex-wrap justify-center gap-2 max-md:contents">{row.map(suggestion_0 => {
          const SuggestionIcon = suggestion_0.icon;
          return <button key={suggestion_0.id} type="button" disabled={disabled} onClick={() => onSelect(suggestion_0.id)} onMouseEnter={() => !disabled && onHoverChange?.(suggestion_0.id)} onMouseLeave={() => onHoverChange?.(null)} onFocus={() => !disabled && onHoverChange?.(suggestion_0.id)} onBlur={() => onHoverChange?.(null)} className={cx("group/suggestion flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-tertiary bg-elevated px-3 text-label-3 transition-colors", disabled ? "cursor-default text-quaternary" : "cursor-pointer text-secondary hover:bg-hover-control hover:text-primary")}><SuggestionIcon className="shrink-0 text-tertiary transition-colors group-hover/suggestion:text-primary" /><span className="whitespace-nowrap">{suggestion_0.label}</span></button>;
        })}</div>;
      $[10] = disabled;
      $[11] = onHoverChange;
      $[12] = onSelect;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = toRows(suggestions).map(t4);
    $[5] = disabled;
    $[6] = onHoverChange;
    $[7] = onSelect;
    $[8] = suggestions;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[14] !== ref || $[15] !== t2 || $[16] !== t3) {
    t4 = <div ref={ref} className="relative flex w-full max-w-[680px] flex-col gap-2 max-md:flex-row max-md:overflow-x-auto max-md:[&::-webkit-scrollbar]:hidden" style={t2}>{t3}</div>;
    $[14] = ref;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};
const SuggestionList = t0 => {
  const $ = _c(14);
  const {
    more,
    ...props
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "home"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("ask-percy", t1);
  const reduceMotion = useReducedMotion() ?? false;
  const {
    suggestions,
    disabled
  } = props;
  if (suggestions.length === 0) {
    return null;
  }
  if (!more) {
    return <PillRows {...props} />;
  }
  const firstCount = ROW_SIZE * FIRST_ROWS;
  const t2 = "relative h-7 w-full max-w-[680px]";
  const t3 = "absolute inset-x-0 top-0 flex flex-col items-center";
  const T0 = AnimatePresence;
  const t4 = false;
  const t5 = more.expanded && <motion.div key="more" initial={reduceMotion ? false : CLOSED} animate={OPEN} exit={reduceMotion ? undefined : CLOSED} className="w-full overflow-hidden"><div className="pb-2"><PillRows {...props} suggestions={suggestions.slice(firstCount)} /></div></motion.div>;
  let t6;
  if ($[1] !== T0 || $[2] !== t5) {
    t6 = <T0 initial={t4}>{t5}</T0>;
    $[1] = T0;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== more.expanded || $[5] !== t) {
    t7 = more.expanded ? t(_temp2) : t(_temp3);
    $[4] = more.expanded;
    $[5] = t;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== disabled || $[8] !== more.toggle || $[9] !== t7) {
    t8 = <div className="pt-1"><LinkButton variant="label-3" onClick={more.toggle} disabled={disabled}>{t7}</LinkButton></div>;
    $[7] = disabled;
    $[8] = more.toggle;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t8) {
    t9 = <div className={t2}><div className={t3}>{t6}{t8}</div></div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return <div className="flex w-full flex-col items-center gap-2"><PillRows {...props} suggestions={suggestions.slice(0, firstCount)} />{t9}</div>;
};
export type { SuggestionItem, SuggestionListProps };
export default SuggestionList;
function _temp(suggestion) {
  return suggestion.id;
}
function _temp2($) {
  return $.showLess;
}
function _temp3($_0) {
  return $_0.showMore;
}